import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { contact } from '../api';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', firm: '', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await contact.submit(form);
      setSent(true);
      toast({ title: 'Message sent', description: 'Our team will get back to you within one business day.' });
      setForm({ name: '', email: '', firm: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send message. Please try again.';
      toast({ title: 'Send failed', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#fdfaf3]">
      <section className="pt-14 pb-10 lg:pt-20 lg:pb-14 bg-gradient-to-b from-[#f4efe4] to-[#fdfaf3] border-b border-stone-200">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest font-semibold text-emerald-800">CONTACT</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold text-stone-900">Let&apos;s talk about your practice.</h1>
          <p className="mt-4 text-lg text-stone-700 max-w-2xl mx-auto">Whether you want a live demo, help with onboarding, or a quick pricing chat &mdash; our SA-based team is a click away.</p>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center"><Mail className="h-5 w-5"/></div>
              <div className="mt-4 font-semibold text-stone-900">Email us</div>
              <div className="mt-1 text-sm text-stone-600">Send us a note and we&apos;ll reply within one business day.</div>
              <div className="mt-2 text-emerald-800 font-medium">info@accudesk.co.za</div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center"><Phone className="h-5 w-5"/></div>
              <div className="mt-4 font-semibold text-stone-900">Call the team</div>
              <div className="mt-1 text-sm text-stone-600">Weekdays, 08:00 &mdash; 17:00 SAST.</div>
              <div className="mt-2 text-emerald-800 font-medium">+27 79 622 6431</div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-6">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center"><MapPin className="h-5 w-5"/></div>
              <div className="mt-4 font-semibold text-stone-900">Visit us</div>
              <div className="mt-1 text-sm text-stone-600">Accu Desk HQ<br/>Alberton, Johannesburg<br/>South Africa, 1449</div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl bg-white border border-stone-200 p-6 lg:p-8 shadow-sm">
              <h2 className="text-2xl font-serif font-semibold text-stone-900">Send us a message</h2>
              <p className="mt-1 text-sm text-stone-600">Tell us a bit about your firm and we&apos;ll come back with next steps.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Kruger" className="mt-1.5"/>
                </div>
                <div>
                  <Label htmlFor="email">Work email</Label>
                  <Input id="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@firm.co.za" className="mt-1.5"/>
                </div>
              </div>
              <div className="mt-4">
                <Label htmlFor="firm">Firm name</Label>
                <Input id="firm" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} placeholder="Kruger &amp; Associates" className="mt-1.5"/>
              </div>
              <div className="mt-4">
                <Label htmlFor="message">How can we help?</Label>
                <Textarea id="message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} placeholder="Tell us about your practice and what you'd like to solve." className="mt-1.5"/>
              </div>
              <div className="mt-6 flex items-center justify-between">
                {sent ? (
                  <div className="flex items-center gap-2 text-emerald-800 text-sm font-medium"><CheckCircle2 size={18}/> Thanks &mdash; we&apos;ll be in touch.</div>
                ) : <div/>}
                <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-6">
                  {busy ? <><Loader2 size={15} className="mr-2 animate-spin"/> Sending...</> : <>Send message <Send size={15} className="ml-2"/></>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
